/**
 * Frontend Control System Types
 * Mirror of backend control types for frontend use
 */

/**
 * Control type enum - mirror of backend ControlType
 */
export enum ControlType {
  // Tier 1: Core Controls
  BLOCK = 'block',
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  SELECT = 'select',
  MULTISELECT = 'multiselect',

  // Tier 2: Advanced Controls
  ARRAY = 'array',
  COLLAPSE = 'collapse',
  LOOKUP = 'lookup',
  UPLOAD = 'upload',
  RICHTEXT = 'richtext',
  DATE = 'date',
  DATETIME = 'datetime',
  TEXTAREA = 'textarea',

  // Tier 3: Specialized Controls
  COLOR = 'color',
  TOGGLE = 'toggle',
  TAB = 'tab',
}

/**
 * Control definition structure
 */
export interface ControlDefinition {
  type: ControlType | string;
  settings?: Record<string, any>;
}

/**
 * Base control metadata
 */
export interface BaseControlMetadata {
  readonly?: boolean;
  placeholder?: string;
  label?: string;
  description?: string;
  hidden?: boolean;
}

// Re-export types from ControlSettings
export type BlockSettings = any;
export type StringSettings = any;
export type NumberSettings = any;
export type BooleanSettings = any;
export type SelectSettings = any;
export type MultiselectSettings = any;
export type ArraySettings = any;
export type LookupSettings = any;
export type UploadSettings = any;
export type RichtextSettings = any;
export type DateSettings = any;
export type DatetimeSettings = any;
export type TextareaSettings = any;
export type ColorSettings = any;
export type ToggleSettings = any;
export type AnyControlSettings = any;
export type TypeSafeControlDefinition = any;

/**
 * Control Component Props Base
 * All control components should accept these common props
 */
export interface ControlComponentProps {
  // Field identification
  name: string;
  path: string; // Dot notation path: "user.address.street"

  // Current value
  value: any;
  onChange: (value: any) => void;

  // Schema definition
  schema: any; // Full schema property definition
  control: any; // x-control definition

  // Error handling
  error?: string;
  touched?: boolean;

  // UI state
  disabled?: boolean;
  readonly?: boolean;

  // Metadata
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;

  // For container controls (block, array) - function to render child controls
  renderChild?: (childProps: {
    schema: any;
    value: any;
    onChange: (value: any) => void;
    fieldPath: string;
  }) => React.ReactNode;
}

/**
 * Render function for a control
 */
export type ControlRenderer = (props: ControlComponentProps) => React.ReactElement | null;

/**
 * Control configuration mapping type to renderer
 */
export interface ControlConfig {
  component: React.ComponentType<ControlComponentProps>;
  label: string;
  description?: string;
  allowedSettings?: string[];
  requiresSettings?: string[];
}

/**
 * Registry of all available controls
 */
export interface ControlRegistry {
  [key: string]: ControlConfig;
}
